import { useState } from "react";
import { ArrowRight, Lock, Wrench, ShieldCheck, RefreshCw, Handshake, Infinity as InfinityIcon } from "lucide-react";

const faqs = [
  {
    id: "modifs-illimitees",
    Icon: InfinityIcon,
    chip: "Les modifications illimitées",
    q: "Que couvrent vraiment les modifications illimitées ?",
    a: "Sont inclus : le remplacement de textes, images, prix ou coordonnées (traités sous 48 h), l'ajout ou la suppression de sections/pages/blocs, les adaptations SEO ponctuelles et la vérification technique après chaque intervention. Restent sur devis à part : une refonte complète, le développement d'une fonctionnalité e-commerce ou back-office, ainsi que la création de contenu (rédaction, photos, tournage). Les demandes sont traitées une à la fois, dans l'ordre d'arrivée.",
    badge: "Modifs illimitées — sans mauvaises surprises.",
  },
  {
    id: "propriete",
    Icon: Lock,
    chip: "La propriété de mon site",
    q: "Qui possède mon site et mes données ?",
    a: "Le nom de domaine est enregistré à votre nom, tous vos contenus (textes, images, données clients) vous appartiennent. Numedia conserve uniquement le code source en maintenance, pour garantir la qualité, la sécurité et les évolutions dans le temps — c'est ce qui nous permet de proposer un service haut de gamme à un tarif accessible.",
    badge: "Votre site vous appartient. À 100 %.",
  },
  {
    id: "rachat",
    Icon: Wrench,
    chip: "Reprendre le code plus tard",
    q: "Et si je veux un jour gérer le code en interne ?",
    a: "Une option de rachat du code source est inscrite dans nos conditions. Vous recevez alors le code complet, la documentation technique et un accompagnement pour transférer la maintenance à un développeur de votre choix. Le tarif est défini à l'avance dans votre lettre de mission, jamais improvisé.",
    badge: "Un jour, vous voudrez peut-être reprendre la main.",
  },
  {
    id: "continuite",
    Icon: ShieldCheck,
    chip: "Si Numedia s'arrête un jour",
    q: "Que se passe-t-il si Numedia s'arrête un jour ?",
    a: "Une clause de continuité protège chaque client : en cas de cessation d'activité, d'indisponibilité prolongée ou d'incapacité durable, le code source complet de votre site vous est remis gratuitement sous 15 jours ouvrables, accompagné d'un dossier technique. Cette garantie est inscrite dans nos conditions générales — votre site reste à vous, quoi qu'il arrive.",
    badge: "Vous êtes déjà protégé.",
  },
  {
    id: "migration",
    Icon: RefreshCw,
    chip: "Reprendre un site existant",
    q: "Pouvez-vous reprendre un site existant ?",
    a: "C'est possible. Après un audit rapide, je vous indique s'il est plus pertinent de refondre le site ou de le faire évoluer. Aucun travail n'est lancé sans cette étape.",
    badge: "On regarde ensemble.",
  },
  {
    id: "modalites",
    Icon: Handshake,
    chip: "Distance ou présentiel",
    q: "Travaillez-vous à distance ou en présentiel ?",
    a: "Les deux. La majorité de mes clients sont accompagnés à distance (visio, partage d'écran), mais je me déplace volontiers pour les projets nécessitant des photos ou un atelier de cadrage sur place.",
    badge: "Selon ce qui vous convient.",
  },
];

export default function FAQ() {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section id="faq" data-testid="faq-section" className="py-20 md:py-28">
      <div className="mx-auto max-w-3xl px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="eyebrow">— Vos questions</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05]">
            Qu'est-ce qui vous{" "}
            <em className="not-italic italic font-light text-aurora">
              préoccupe le plus
            </em>
            <br />
            avant de nous lancer ?
          </h2>
          <p className="mt-6 text-[#0F172A]/70 leading-relaxed max-w-xl mx-auto text-base md:text-lg">
            Choisissez la préoccupation qui vous parle — la réponse s'affiche
            juste en dessous.
          </p>
        </div>

        {/* Selector list */}
        <div className="space-y-3">
          {faqs.map((f) => (
            <FaqItem
              key={f.id}
              faq={f}
              isOpen={openId === f.id}
              onToggle={() => toggle(f.id)}
            />
          ))}
        </div>

        {/* Hint (visible only when nothing is open) */}
        {openId === null && (
          <p
            data-testid="faq-hint"
            className="mt-8 text-center text-[#0F172A]/40 italic text-sm"
          >
            ↑ Cliquez une préoccupation pour découvrir la réponse
          </p>
        )}

        {/* Fallback CTA */}
        <p className="mt-14 text-center text-[#0F172A]/60 text-sm md:text-base">
          Une autre question ?{" "}
          <a
            href="#reservation"
            data-testid="faq-fallback-cta"
            className="font-semibold text-[#6366F1] underline underline-offset-4 decoration-[#A855F7]/40 hover:decoration-[#A855F7] transition"
          >
            Posez-la directement lors de l'appel découverte →
          </a>
        </p>
      </div>
    </section>
  );
}

function FaqItem({ faq, isOpen, onToggle }) {
  const { id, Icon, chip, q, a, badge } = faq;
  return (
    <div data-testid={`faq-item-${id}`} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`faq-panel-${id}`}
        data-testid={`faq-chip-${id}`}
        className={`relative w-full flex items-center gap-4 px-5 py-4 md:py-5 rounded-2xl text-left transition-all duration-300 shadow-sm hover-lift ${
          isOpen
            ? "bg-gradient-to-br from-[#F5F3FF] to-[#FDF2F8]"
            : "bg-white hover:bg-black/[.02]"
        }`}
      >
        {/* Aurora border */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{
            padding: "1.5px",
            background:
              "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            opacity: isOpen ? 1 : 0.5,
          }}
        />
        {/* Icon */}
        <span
          className="relative flex items-center justify-center h-11 w-11 rounded-xl shrink-0"
          style={{
            background: isOpen
              ? "linear-gradient(135deg,#6366F1 0%,#A855F7 50%,#EC4899 100%)"
              : "rgba(168,85,247,0.08)",
          }}
        >
          <Icon
            size={20}
            className={isOpen ? "text-white" : "text-[#A855F7]"}
            strokeWidth={2}
          />
        </span>
        {/* Label */}
        <span className="relative flex-1 font-display text-base md:text-lg text-[#0B1027] font-medium leading-tight">
          {chip}
        </span>
        {/* Arrow */}
        <ArrowRight
          size={20}
          className={`relative shrink-0 text-[#A855F7] transition-transform duration-300 ${
            isOpen ? "rotate-90" : ""
          }`}
          strokeWidth={2}
        />
      </button>

      {/* Panel */}
      <div
        id={`faq-panel-${id}`}
        role="region"
        aria-hidden={!isOpen}
        className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
        style={{
          maxHeight: isOpen ? "600px" : "0px",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div
          data-testid={`faq-answer-${id}`}
          className="mt-3 mx-2 rounded-2xl bg-gradient-to-br from-[#F5F3FF] to-[#FDF2F8] p-6 md:p-7"
          style={{
            border: "1px solid rgba(168,85,247,0.18)",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600">
              Rassuré ✓
            </span>
          </div>
          <h3 className="font-display text-lg md:text-xl text-[#0B1027] leading-snug">
            {badge}
          </h3>
          <p className="mt-3 text-[#0F172A]/75 text-sm md:text-base leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}
